import { http, normaliseError } from './client'

import type { AuthResultDto, LoginRequest, UserDto } from '@/types/api'

/** `POST /auth/login` → `{ accessToken, refreshToken, expiresInSeconds, user }`. */
export async function login(body: LoginRequest): Promise<AuthResultDto> {
  try {
    // A 401 here is a wrong password, not an expired session — never trigger a refresh.
    const response = await http.post<AuthResultDto>('/auth/login', body, {
      _uoSkipAuthRefresh: true,
    })
    return response.data
  } catch (error) {
    throw normaliseError(error)
  }
}

/** `POST /auth/refresh` — rotating: the presented refresh token is invalidated. */
export async function refresh(refreshToken: string): Promise<AuthResultDto> {
  try {
    const response = await http.post<AuthResultDto>(
      '/auth/refresh',
      { refreshToken },
      { _uoSkipAuthRefresh: true },
    )
    return response.data
  } catch (error) {
    throw normaliseError(error)
  }
}

/** `POST /auth/logout` → 204. */
export async function logout(refreshToken: string): Promise<void> {
  try {
    await http.post('/auth/logout', { refreshToken }, { _uoSkipAuthRefresh: true })
  } catch (error) {
    throw normaliseError(error)
  }
}

/** `GET /auth/me` → `user`. */
export async function me(): Promise<UserDto> {
  try {
    const response = await http.get<UserDto>('/auth/me')
    return response.data
  } catch (error) {
    throw normaliseError(error)
  }
}

/**
 * `POST /auth/change-password` → a fresh `{ accessToken, refreshToken, user }` for THIS
 * session. Every other session the user had is revoked server-side.
 *
 * A wrong current password comes back as 422 `CURRENT_PASSWORD_INCORRECT`, never 401,
 * so the client's refresh-then-sign-out path is not triggered by a typo.
 */
export async function changePassword(body: {
  currentPassword: string
  newPassword: string
}): Promise<AuthResultDto> {
  try {
    const response = await http.post<AuthResultDto>('/auth/change-password', body)
    return response.data
  } catch (error) {
    throw normaliseError(error)
  }
}
